import { Grid, GridItem } from '@chakra-ui/react';
import { Button, Icons, SelectOption, SvgSpriteConsumer } from '@pluxee-design-system/core';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import { SelectControl } from 'common/forms';
import { I18nProps, withTranslations } from 'i18n';
import { useCallback } from 'react';
import { ConfigField } from 'types/config';

interface OnlineTerminalRowProps extends I18nProps {
  config: Record<string, ConfigField>;
  disabled?: boolean;
  idPrefix: string;
  index: number;
  name: string;
  onRemove: (index: number) => void;
  providers: SelectOption[];

  // Based on acquirer
  canRemove?: boolean;
  isMandatory?: boolean;
}

const OnlineTerminalRow = ({
  canRemove = true,
  config,
  disabled,
  idPrefix,
  index,
  isMandatory,
  name,
  onRemove,
  providers,
  t,
}: OnlineTerminalRowProps) => {
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  const columnsSize = config.terminalIdOnline.visible
    ? config.terminalProviderOnline.visible
      ? 2
      : 1
    : 0;

  return (
    <Grid
      key={`POSOnline-field-${index}`}
      templateAreas={{
        base: `"input${canRemove ? ' button' : ''}"
        "select${canRemove ? ' button' : ''}"`,
        md: `"input select${canRemove ? ' button' : ''}"`,
      }}
      templateColumns={{
        base: `1fr${canRemove ? ' 70px' : ''}`,
        md: `repeat(${columnsSize}, 1fr)${canRemove ? ' 70px' : ''}`,
      }}
      gap={4}
    >
      {config.terminalIdOnline.visible && (
        <GridItem width="100%" area="input">
          <InputControl
            type="text"
            disabled={disabled}
            mandatory={isMandatory}
            name={`${name}terminalId`}
            id={`${idPrefix}${name}terminalId`}
            placeholder={t('location_add.form.terminalIdOnline.label', 'Supplier ID')}
            title={t('location_add.form.terminalIdOnline.label', 'Supplier ID')}
          />
        </GridItem>
      )}

      {config.terminalProviderOnline.visible && (
        <GridItem width="100%" area="select">
          <SelectControl
            disabled={disabled}
            mandatory={isMandatory}
            name={`${name}terminalProvider`}
            id={`${idPrefix}${name}terminalProvider`}
            placeholder={t('location_add.form.terminalProviderOnline.label', 'Provider')}
            title={t('location_add.form.terminalProviderOnline.label', 'Provider')}
            notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
            options={providers}
            isClearable={!isMandatory}
          />
        </GridItem>
      )}
      {canRemove && (
        <GridItem width="70px" mt={7} area="button">
          <Button
            type="button"
            onClick={handleRemove}
            variant="secondaryTextOnly"
            id={`${idPrefix}${name}deleteButton`}
            size="md"
            alignSelf="flex-end"
          >
            <SvgSpriteConsumer size={24} svgId={Icons.TRASH24} />
          </Button>
        </GridItem>
      )}
    </Grid>
  );
};

export default withTranslations(OnlineTerminalRow);
